import { $enum } from 'ts-enum-util'
import * as yup from 'yup'
import { DriverSettingsType, DriverStatusType } from 'apps/fleet-planner/types'
import * as Types from 'apps/fleet-planner/types'

export const REQUIRED_MESSAGE = 'This field is required.'

/**
 * Requires the values (firstName, lastName) in the first item in array only
 * However, if a user enters a `firstName` value for an item, then `lastName` is also required
 * @see https://fp-staging.transfix.io/api-docs/index.html#/Drivers/post_drivers
 */
export const DriverSchema = yup
  .object<Types.IDriver>({
    firstName: yup
      .string()
      .test('is-required', REQUIRED_MESSAGE, function validateFirstName(
        value: yup.ValidationError
      ) {
        return !this.path.includes('drivers[0]') || value
      }),
    lastName: yup
      .string()
      .test('is-required', REQUIRED_MESSAGE, function validateLastName(value: yup.ValidationError) {
        return !this.path.includes('drivers[0]') || value
      })
      .when('firstName', {
        is: val => !!val,
        then: yup.string().required('Please enter a last name.'),
        otherwise: yup.string(),
      }),
    motorCarrier: yup.object<Types.ICarrier>({
      id: yup.number(),
      mcNumber: yup.number(),
      dotNumber: yup.string(),
      name: yup.string(),
    }),
    email: yup.string().email('Please enter a valid email').notRequired(),
    cellPhone: yup.string().notRequired(),
    status: yup
      .mixed()
      .oneOf([...$enum(DriverStatusType).getValues()])
      .default(DriverStatusType.active),
    settings: yup.object<DriverSettingsType>({
      smsStatusUpdatesEnabled: yup.boolean(),
    }),
  })
  .required()

export type DriverSchemaType = yup.InferType<typeof DriverSchema>

export const DriversSchema = yup.array().of(DriverSchema).required()
export type DriversSchemaType = yup.InferType<typeof DriversSchema>

/**
 * Generates request for POST /drivers from form output
 * @param data - Default form output
 */
export const createDriverRequest = (data: DriverSchemaType): Types.IDriver => {
  const { cellPhone, email, firstName, lastName, motorCarrier, settings, status } = data || {}
  const { id = 0, name = '' } = motorCarrier || {}
  const { smsStatusUpdatesEnabled = false, suppressSmsStatusUpdatesOptIn = false } = settings || {}

  return {
    firstName,
    lastName,
    motorCarrierId: id,
    motorCarrier: {
      id,
      name,
    },
    email,
    cellPhone,
    status,
    settings: {
      smsStatusUpdatesEnabled,
      suppressSmsStatusUpdatesOptIn,
    },
  }
}
